"use client";

import { useState } from "react";
import { useQuery, useAction, useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { EmailListItem } from "@/components/email/email-list-item";
import { SentEmailListItem } from "@/components/email/sent-email-list-item";
import { EmailThread } from "@/components/email/email-thread";
import { GmailConnectButton } from "@/components/email/gmail-connect-button";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useGmailAuth } from "@/hooks/use-gmail-auth";
import { PenSquare, RefreshCw, Loader2, Inbox, RotateCcw, CheckCheck, Trash2, Send } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router";

export default function EmailInbox() {
  const navigate = useNavigate();
  const { connection } = useGmailAuth();
  const [activeTab, setActiveTab] = useState<"inbox" | "sent">("inbox");
  const receivedEmails = useQuery(api.email.sync.getReceivedEmails, { limit: 50 });
  const sentEmails = useQuery(api.email.sync.getSentEmails, { limit: 50 });
  const triggerSync = useAction(api.email.sync.triggerSync);
  const clearAndResync = useAction(api.email.sync.clearAndResync);
  const markAllAsRead = useMutation(api.email.sync.markAllAsRead);
  const cleanupOldEmails = useMutation(api.email.sync.cleanupOldEmails);
  const [syncing, setSyncing] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [selectedThread, setSelectedThread] = useState<string | null>(null);

  const unreadCount = receivedEmails?.filter((e: { read: boolean }) => !e.read).length ?? 0;

  const handleSync = async () => {
    setSyncing(true);
    try {
      await triggerSync();
    } catch (error) {
      console.error("Sync failed:", error);
    } finally {
      setSyncing(false);
    }
  };

  const handleResync = async () => {
    setResyncing(true);
    try {
      await clearAndResync();
    } catch (error) {
      console.error("Resync failed:", error);
    } finally {
      setResyncing(false);
    }
  };

  if (!connection) {
    return (
      <div className="min-h-screen bg-background">
        <div className="sticky top-0 z-10 bg-background border-b px-4 pt-safe pb-3">
          <h1 className="text-lg font-semibold">Messages</h1>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-center text-muted-foreground">
            Connect your Gmail account to view messages
          </p>
          <GmailConnectButton />
        </div>
      </div>
    );
  }

  if (selectedThread) {
    return (
      <EmailThread
        threadId={selectedThread}
        onBack={() => setSelectedThread(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-10 bg-background border-b px-4 pt-safe pb-3">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-lg font-semibold">Messages</h1>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={syncing || resyncing}
                >
                  {syncing || resyncing ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-5 w-5" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleSync} disabled={syncing || resyncing}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Sync New
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleResync} disabled={syncing || resyncing}>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Clear & Resync All
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => markAllAsRead()} disabled={unreadCount === 0}>
                  <CheckCheck className="h-4 w-4 mr-2" />
                  Mark All Read
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => cleanupOldEmails()}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Old (30+ days)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/email/compose")}
            >
              <PenSquare className="h-5 w-5" />
            </Button>
          </div>
        </div>
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "inbox" | "sent")}>
          <TabsList className="w-full">
            <TabsTrigger value="inbox" className="flex-1 gap-2">
              <Inbox className="h-4 w-4" />
              Inbox
              {unreadCount > 0 && (
                <span className="bg-primary text-primary-foreground text-xs px-1.5 py-0.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="sent" className="flex-1 gap-2">
              <Send className="h-4 w-4" />
              Sent
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {activeTab === "inbox" ? (
        receivedEmails?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <Inbox className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              No messages yet. Emails from support will appear here.
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate("/email/compose")}
            >
              <PenSquare className="h-4 w-4 mr-2" />
              Compose Email
            </Button>
          </div>
        ) : (
          <div>
            {receivedEmails?.map((email) => (
              <EmailListItem
                key={email._id}
                email={email}
                onClick={() => setSelectedThread(email.gmailThreadId)}
              />
            ))}
          </div>
        )
      ) : (
        sentEmails?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <Send className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              No sent messages yet.
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate("/email/compose")}
            >
              <PenSquare className="h-4 w-4 mr-2" />
              Compose Email
            </Button>
          </div>
        ) : (
          <div>
            {sentEmails?.map((email) => (
              <SentEmailListItem
                key={email._id}
                email={email}
                onClick={() => setSelectedThread(email.gmailThreadId)}
              />
            ))}
          </div>
        )
      )}
    </div>
  );
}
